import { Box, Button, Flex, Grid, Heading, LinkBox, Stack, Text } from "@biblioteksentralen/react";
import { getPath, isProduction, ResolvedLitteraturprisBannerData } from "@libry-content/common";
import { logClick } from "../../../utils/analytics/Plausible";
import { Edit } from "../../editInSanity/EditInSanity";
import { MissingCampaignTemplateContentBanner } from "../MissingCampaignTemplateContentBanner";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { SanityImagePreservingAspectRatio } from "../../images/SanityImagePreservingAspectRatio";
import { ExternalLink } from "react-feather";
import { getContainerQuery } from "../../site/banners/promotedItems/utils";

export type LitteraturprisBannerProps = {
  litteraturpris: ResolvedLitteraturprisBannerData | undefined | null;
  onLandingPage?: boolean;
};

export const LitteraturprisBanner = ({ litteraturpris, onLandingPage }: LitteraturprisBannerProps) => {
  const templateContent = litteraturpris?.templateContent;
  const missingContent = !templateContent?.logo?.asset;

  if (missingContent) console.error("Missing necessary assets to display bibliobattles banner");

  if (missingContent && !isProduction) {
    return (
      <MissingCampaignTemplateContentBanner
        campaignName="Litteraturpris"
        templatePath="cms/structure/campaigns;litteraturprisTemplateContent"
      />
    );
  }

  if (!templateContent) {
    return null;
  }

  return (
    <Grid
      maxW="container.lg"
      sx={{
        containerType: "inline-size",
      }}
    >
      <LinkBox onClick={() => logClick("Litteraturprisbanner")} textDecoration="none" role="group" width="100%">
        <Flex background="#43533D" borderRadius={{ base: "0", md: ".5rem" }} height="100%">
          <Content litteraturpris={litteraturpris} onLandingPage={onLandingPage} />
        </Flex>
      </LinkBox>
    </Grid>
  );
};

const Content = ({
  litteraturpris,
  onLandingPage,
}: {
  litteraturpris: ResolvedLitteraturprisBannerData;
  onLandingPage?: boolean;
}) => {
  const { t, ts } = useTranslation();

  if (!litteraturpris?.startDate) return null;

  return (
    <Grid
      sx={getContainerQuery({
        base: { gridTemplateColumns: "1fr 2fr 1fr", gridTemplateRows: "1fr 1fr", padding: "1rem 1rem" },
        sm: { gridTemplateColumns: "2fr 0.5fr 4fr", padding: "2rem 3rem 3rem 2rem" },
        md: { gridTemplateColumns: "2fr .2fr 4fr", padding: "2rem 6rem 4rem 4rem" },
      })}
    >
      <Box
        alignSelf="center"
        sx={getContainerQuery({
          base: { gridColumn: "2/3", gridRow: "1/2" },
          sm: { gridColumn: "1/3", gridRow: "1/3" },
        })}
      >
        <SanityImagePreservingAspectRatio
          image={litteraturpris.templateContent?.logo}
          resolution={230}
          boxShadow="unset"
        />
      </Box>
      <Flex
        alignItems="flex-end"
        sx={getContainerQuery({
          base: { gridColumn: "1/4", gridRow: "2/3" },
          sm: { gridColumn: "2/4", gridRow: "1/3" },
        })}
      >
        <Stack spacing="1rem">
          <Heading
            as="h2"
            color="white"
            sx={getContainerQuery({
              base: { fontSize: "lg" },
              sm: { fontSize: "1.5rem" },
              md: { fontSize: "2.4rem" },
            })}
          >
            {ts(litteraturpris.templateContent.bannerHeader)}
          </Heading>
          <Text
            color="white"
            sx={getContainerQuery({
              base: { fontSize: "1rem" },
              sm: { fontSize: "1.2rem" },
              md: { fontSize: "1.5rem" },
            })}
            marginBottom={{ base: "0", sm: "0.5rem" }}
          >
            {ts(litteraturpris.templateContent.bannerText)}
          </Text>
          <Stack
            sx={getContainerQuery({
              base: { alignItems: "center" },
              sm: { alignItems: "flex-start" },
            })}
          >
            {onLandingPage && (
              <Button
                as="a"
                href={
                  litteraturpris.templateContent.buttonUrl && !litteraturpris.separatePage
                    ? litteraturpris.templateContent.buttonUrl
                    : getPath(litteraturpris)
                }
                variant="primary"
                sx={getContainerQuery({
                  base: { fontSize: "md" },
                  sm: { fontSize: "lg" },
                  md: { fontSize: "xl" },
                })}
                fontWeight="semibold"
                rightIcon={
                  litteraturpris.templateContent.buttonUrl && !litteraturpris.separatePage ? (
                    <ExternalLink size="1em" />
                  ) : undefined
                }
                background="#FCB973"
                color="#43533D"
              >
                {litteraturpris.separatePage ? t("Les mer") : ts(litteraturpris.templateContent.buttonText)}
              </Button>
            )}
            <Edit doc={litteraturpris} />
          </Stack>
        </Stack>
      </Flex>
    </Grid>
  );
};
